import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Badge } from '@/components/ui/badge';
import { BarChart3, ClipboardCheck, Star, TrendingUp, Trophy, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  lesson: 'Lecciones', task: 'Tareas', correction: 'Correcciones',
  evaluation: 'Evaluaciones', reinforcement: 'Refuerzo'
};
const TYPE_COLORS = {
  lesson: '#3B82F6', task: '#F59E0B', correction: '#10B981',
  evaluation: '#EF4444', reinforcement: '#8B5CF6'
};

function StatCard({ icon: Icon, label, value, sub, color = 'text-blue-600' }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid={`stat-${label}`}>
      <div className="flex items-center gap-2 text-slate-400 text-xs font-bold uppercase tracking-wider mb-2">
        <Icon className="w-4 h-4" />{label}
      </div>
      <p className={`text-3xl font-black ${color}`} style={{ fontFamily: 'Montserrat' }}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function SubmissionBar({ label, type, stats }) {
  const total = stats.total_expected || 0;
  const submittedPct = total ? (stats.submitted / total) * 100 : 0;
  const pendingPct = total ? (stats.pending / total) * 100 : 0;
  const cancelledPct = total ? (stats.cancelled / total) * 100 : 0;
  const color = TYPE_COLORS[type] || '#64748B';

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4" data-testid={`sub-row-${type}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
          <p className="font-semibold text-sm text-slate-700">{label}</p>
          <Badge variant="outline" className="text-[10px]">{stats.completion_rate}%</Badge>
        </div>
        <p className="text-xs text-slate-400">
          {stats.submitted}/{total} entregadas
        </p>
      </div>
      {total > 0 && (
        <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100">
          <div className="bg-emerald-500" style={{ width: `${submittedPct}%` }} title={`Entregadas: ${stats.submitted}`} />
          <div className="bg-amber-400" style={{ width: `${cancelledPct}%` }} title={`Anuladas: ${stats.cancelled}`} />
          <div className="bg-slate-300" style={{ width: `${pendingPct}%` }} title={`Pendientes: ${stats.pending}`} />
        </div>
      )}
      <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Enviadas {stats.submitted}</span>
        {stats.resubmitted > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />Reenviadas {stats.resubmitted}</span>}
        {stats.cancelled > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-300" />Anuladas {stats.cancelled}</span>}
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300" />Pendientes {stats.pending}</span>
      </div>
    </div>
  );
}

export default function AnalyticsTab({ classId }) {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/analytics/teacher/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  }, [classId, token]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="analytics-loading">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-500" data-testid="analytics-error">{error}</div>
    );
  }

  if (!data || data.total_students === 0) {
    return (
      <div className="text-center py-16 text-slate-400" data-testid="analytics-empty">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p className="font-semibold text-slate-500">Sin estudiantes en esta clase</p>
        <p className="text-sm">Añade estudiantes para ver estadísticas.</p>
      </div>
    );
  }

  const subTypes = Object.keys(data.submissions.by_type);

  return (
    <div data-testid="analytics-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-700" style={{ fontFamily: 'Montserrat' }}>
            Estadísticas de la clase
          </h3>
          <p className="text-xs text-slate-400">{data.class_name}</p>
        </div>
        <button
          onClick={fetchAnalytics}
          className="text-xs text-blue-600 hover:underline"
          data-testid="analytics-refresh-btn"
        >
          Actualizar
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Estudiantes" value={data.total_students} color="text-slate-700" />
        <StatCard icon={Star} label="XP promedio" value={data.xp.average} sub="puntos por estudiante" color="text-amber-500" />
        <StatCard
          icon={Trophy}
          label="Top XP"
          value={data.xp.top[0]?.xp ?? 0}
          sub={data.xp.top[0]?.name || '—'}
          color="text-purple-600"
        />
        <StatCard
          icon={TrendingUp}
          label="Promedio evaluaciones"
          value={data.evaluations.overall_average !== null ? data.evaluations.overall_average : '—'}
          sub="sobre 10"
          color="text-emerald-600"
        />
      </div>

      {/* Submissions by type */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ClipboardCheck className="w-4 h-4 text-slate-500" />
          <h4 className="font-bold text-slate-700">Estado de entregas</h4>
        </div>
        {subTypes.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Aún no hay contenido publicado.</p>
        ) : (
          <div className="space-y-3">
            {subTypes.map(type => (
              <SubmissionBar
                key={type}
                type={type}
                label={TYPE_LABELS[type] || type}
                stats={data.submissions.by_type[type]}
              />
            ))}
          </div>
        )}
      </div>

      {/* Top XP table */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-4 h-4 text-amber-500" />
          <h4 className="font-bold text-slate-700">Ranking de XP</h4>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          {data.xp.top.length === 0 ? (
            <p className="text-sm text-slate-400 italic p-4">Sin datos de XP aún.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold w-10">#</th>
                  <th className="text-left px-4 py-2 font-semibold">Estudiante</th>
                  <th className="text-left px-4 py-2 font-semibold">Nivel</th>
                  <th className="text-right px-4 py-2 font-semibold">XP</th>
                </tr>
              </thead>
              <tbody>
                {data.xp.top.map((r, i) => (
                  <tr key={r.student_id} className="border-t border-slate-100" data-testid={`xp-row-${i}`}>
                    <td className="px-4 py-2 text-slate-400 font-semibold">{i + 1}</td>
                    <td className="px-4 py-2 font-medium text-slate-700">{r.name}</td>
                    <td className="px-4 py-2 text-slate-500">{r.level}</td>
                    <td className="px-4 py-2 text-right font-black text-amber-600" style={{ fontFamily: 'Montserrat' }}>{r.xp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Evaluation averages */}
      {data.evaluations.items.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-emerald-500" />
            <h4 className="font-bold text-slate-700">Promedio por evaluación</h4>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Evaluación</th>
                  <th className="text-right px-4 py-2 font-semibold">Calificadas</th>
                  <th className="text-right px-4 py-2 font-semibold">Promedio</th>
                </tr>
              </thead>
              <tbody>
                {data.evaluations.items.map(e => (
                  <tr key={e.id} className="border-t border-slate-100" data-testid={`eval-row-${e.id}`}>
                    <td className="px-4 py-2 font-medium text-slate-700">{e.title}</td>
                    <td className="px-4 py-2 text-right text-slate-500">{e.graded_count}/{e.total_assigned}</td>
                    <td className="px-4 py-2 text-right font-black text-emerald-600" style={{ fontFamily: 'Montserrat' }}>
                      {e.average !== null ? e.average : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
